import { useState, type ComponentType, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Menu,
    LogOut,
    User,
    SquarePlus,
    Users,
    UsersRound,
    Megaphone,
    Shield,
    List,
    FileText,
    Search,
    CircleDollarSign,
    ListOrdered,
    CalendarDays,
    Handshake,
    LayoutGrid,
    Plus,
    Send,
} from 'lucide-react';
import { UnknownFunction, User as AppUser } from '../shared/types';
import { generateNewDocId } from '../services/firebase';
import { canCreateCampaign } from '../utils/campaignPermissions';
import { canBrowseOpportunities } from '../utils/pitching/permissions';
import { Button } from '@/components/ui/button';
import {
    Sheet,
    SheetContent,
    SheetTitle,
} from '@/components/ui/sheet';
import { cn } from "cn";

interface PrimaryNavigationProps {
    signOut: UnknownFunction;
    user?: AppUser;
}

type NavIcon = ComponentType<{ className?: string }>;

function NavItem({
    icon: Icon,
    label,
    onClick,
}: {
    icon: NavIcon;
    label: string;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="flex w-full items-center gap-4 px-4 py-2.5 text-left text-sm hover:bg-muted"
        >
            <Icon className="size-6 shrink-0" />
            <span>{label}</span>
        </button>
    );
}

function NavSectionLabel({ children }: { children: ReactNode }) {
    return (
        <div className="px-4 py-2 text-xs font-medium tracking-wide text-muted-foreground uppercase">
            {children}
        </div>
    );
}

function NavDivider() {
    return <div className="my-4 h-px bg-border" role="separator" />;
}

export default function PrimaryNavigation(props: PrimaryNavigationProps) {
    const { signOut, user } = props;
    const [drawerOpen, setDrawerOpen] = useState(false);
    const navigate = useNavigate();

    const isGrailTeam = user?.roles?.manager || user?.roles?.admin || user?.roles?.externalManager || user?.roles?.superAdmin || user?.roles?.salesTeam || user?.roles?.brandTeam;

    const close = () => setDrawerOpen(false);

    const go = (path: string) => {
        close();
        navigate(path);
    };

    const openExternal = (url: string) => {
        close();
        window.open(url, '_blank');
    };

    return (
        <>
            <Button
                variant="ghost"
                size="icon"
                aria-label="open drawer"
                onClick={() => setDrawerOpen(true)}
                className="text-foreground hover:bg-black/10"
            >
                <Menu className="size-6" />
            </Button>
            <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
                <SheetContent
                    side="right"
                    className={cn(
                        'w-[calc(100vw-3rem)] max-w-85 gap-0 p-0 sm:max-w-85'
                    )}
                    showCloseButton={false}
                >
                    <SheetTitle className="sr-only">Primary navigation</SheetTitle>
                    <div className="flex h-full flex-col justify-between overflow-y-auto py-2">
                        <nav className="flex flex-col" aria-label="Main">

                            {/* SECTION: Primary */}
                            {(isGrailTeam || user?.roles?.booker) && (
                                <NavItem
                                    icon={SquarePlus}
                                    label="New Booking"
                                    onClick={() => go('/new-booking')}
                                />
                            )}

                            {isGrailTeam && (
                                <>
                                    <NavItem
                                        icon={Search}
                                        label="Search Bookings"
                                        onClick={() => go('/search')}
                                    />
                                </>
                            )}

                            {user?.roles?.creator ? (
                                <>
                                    <NavItem
                                        icon={Users}
                                        label="Account Details"
                                        onClick={() => {
                                            if (user?.talentIds?.length === 1) {
                                                close();
                                                window.open(
                                                    `/edit-talent/${user?.talentIds[0]}`,
                                                    '_blank'
                                                );
                                            } else {
                                                go(`/creator-roster/${user?.id}`);
                                            }
                                        }}
                                    />
                                    <NavItem
                                        icon={CircleDollarSign}
                                        label="Request Financing"
                                        onClick={() =>
                                            openExternal(
                                                'https://airtable.com/appRfXchS5VSDedJY/shrJbI59R0sk8PhWv'
                                            )
                                        }
                                    />
                                </>
                            ) : null}

                            {/* SECTION: CAMPAIGNS */}
                            {(isGrailTeam || user?.roles?.booker) && (
                                <>
                                    <NavDivider />
                                    <NavSectionLabel>Campaigns</NavSectionLabel>
                                    {user?.roles?.booker && (
                                        <NavItem
                                            icon={Megaphone}
                                            label="My Campaigns"
                                            onClick={() => go('/campaigns')}
                                        />
                                    )}

                                    {canBrowseOpportunities(user) && (
                                        <NavItem
                                            icon={LayoutGrid}
                                            label="Open for Pitching"
                                            onClick={() => go('/opportunities')}
                                        />
                                    )}

                                    {canCreateCampaign(user) && (
                                        <NavItem
                                            icon={Plus}
                                            label="New Opportunity"
                                            onClick={() => go('/new-opportunity')}
                                        />
                                    )}

                                    {canBrowseOpportunities(user) && (
                                        <NavItem
                                            icon={Send}
                                            label="My Pitches"
                                            onClick={() => go('/my-pitches')}
                                        />
                                    )}
                                </>
                            )}

                            {/* SECTION: ROSTER */}
                            {isGrailTeam && (
                                <>
                                    <NavDivider />
                                    <NavSectionLabel>Roster & Lists</NavSectionLabel>
                                    <NavItem
                                        icon={Users}
                                        label="My Roster"
                                        onClick={() => go(`/roster/${user?.id}`)}
                                    />
                                    <NavItem
                                        icon={UsersRound}
                                        label="Grail Roster"
                                        onClick={() => go('/roster/')}
                                    />
                                    <NavItem
                                        icon={List}
                                        label="My Lists"
                                        onClick={() => go(`/lists/${user.id}`)}
                                    />
                                </>
                            )}

                            {/* SECTION: CREATOR REQUEST FORMS */}
                            {user?.roles?.booker && (
                                <>
                                    <NavDivider />
                                    <NavSectionLabel>Creator Request Forms</NavSectionLabel>
                                    <NavItem
                                        icon={CalendarDays}
                                        label="In-Person Events"
                                        onClick={() =>
                                            openExternal(
                                                'https://airtable.com/appRfXchS5VSDedJY/shrXF3CtnvzJYPfhJ'
                                            )
                                        }
                                    />
                                    <NavItem
                                        icon={ListOrdered}
                                        label="Curated Creator Lists"
                                        onClick={() =>
                                            openExternal(
                                                'https://airtable.com/appRfXchS5VSDedJY/shrNhMMZri9RYBgO0'
                                            )
                                        }
                                    />
                                </>
                            )}

                            {/* SECTION: MANAGER RESOURCES */}
                            {isGrailTeam && (
                                <>
                                    <NavDivider />
                                    <NavSectionLabel>Manager Resources</NavSectionLabel>
                                    <NavItem
                                        icon={FileText}
                                        label="Grail Circle"
                                        onClick={() =>
                                            openExternal('https://grail-talent.circle.so/')
                                        }
                                    />
                                    <NavItem
                                        icon={FileText}
                                        label="Payment Queries"
                                        onClick={() =>
                                            openExternal(
                                                'https://airtable.com/appRfXchS5VSDedJY/shrSmoVHHPTImoAgc'
                                            )
                                        }
                                    />
                                    <NavItem
                                        icon={FileText}
                                        label="Grail CRM"
                                        onClick={() =>
                                            openExternal(
                                                'https://airtable.com/appRfXchS5VSDedJY/shrM3V1Opm7h8JAHc'
                                            )
                                        }
                                    />
                                    <NavItem
                                        icon={FileText}
                                        label="Client Directory"
                                        onClick={() =>
                                            openExternal(
                                                'https://airtable.com/appRfXchS5VSDedJY/pagvP6sewRbOopbuw?KzWLA=recMot9vkMY9Uk1YF'
                                            )
                                        }
                                    />
                                    <NavItem
                                        icon={FileText}
                                        label="Creator Interface"
                                        onClick={() =>
                                            openExternal(
                                                'https://airtable.com/appRfXchS5VSDedJY/pag00yXYayBsF0ptF?EA7SH=recB0qdU9JASiiKXu'
                                            )
                                        }
                                    />
                                </>
                            )}

                            {/* SECTION: ADMIN TOOLS */}
                            {user?.roles?.admin ? (
                                <>
                                    <NavDivider />
                                    <NavSectionLabel>Admin Tools</NavSectionLabel>
                                    <NavItem
                                        icon={Shield}
                                        label="Managers List"
                                        onClick={() => go('/managers')}
                                    />
                                    <NavItem
                                        icon={Handshake}
                                        label="New Client"
                                        onClick={() => {
                                            const newClientId = generateNewDocId('clients');
                                            go(`/edit-client/${newClientId}`);
                                        }}
                                    />
                                </>
                            ) : null}
                            {user?.roles?.superAdmin ? (
                                <>
                                    <NavItem
                                        icon={Shield}
                                        label="Airtable ops stats"
                                        onClick={() => go('/admin/airtable-operations')}
                                    />
                                    <NavItem
                                        icon={Shield}
                                        label="Test"
                                        onClick={() => go('/dev/test')}
                                    />
                                </>
                            ) : null}

                        </nav>
                        <nav className="flex flex-col" aria-label="Account">
                            <NavDivider />
                            <NavItem
                                icon={FileText}
                                label="Terms"
                                onClick={() =>
                                    openExternal('https://grail-talent.com/terms')
                                }
                            />
                            {user?.id ? (
                                <NavItem
                                    icon={LogOut}
                                    label="Log Out"
                                    onClick={() => {
                                        close();
                                        signOut();
                                    }}
                                />
                            ) : (
                                <NavItem
                                    icon={User}
                                    label="Sign Up"
                                    onClick={() => go('/')}
                                />
                            )}
                            <div className="p-1 text-center text-tiny/4">
                                v{import.meta.env.VITE_APP_VERSION}
                            </div>
                        </nav>
                    </div>
                </SheetContent>
            </Sheet>
        </>
    );
}
